/**
 * Resume Payment Banner
 * Shows pending payment/subscription banners at the top of the layout
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UButton from 'ubs-framework/v2/UButton';
import useCashfreePayment from '../../manage-account/billing/common/useCashfreePayment';

// Returns true if the card expires within the next 2 months
const isCardExpiringSoon = (paymentMethodInfo) => {
    if (paymentMethodInfo?.type !== 'CARD') return false;
    const { expiryMonth, expiryYear } = paymentMethodInfo?.card || {};
    if (!expiryMonth || !expiryYear) return false;
    const today = new Date();
    const expiryDate = new Date(2000 + parseInt(expiryYear, 10), parseInt(expiryMonth, 10) - 1, 1);
    const twoMonthsLater = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    return expiryDate <= twoMonthsLater;
};

const ResumePaymentBanner = () => {
    const history = useHistory();
    const _ORG_ID = useSelector(e => e.common?.orgId);
    const _RESUME_PAYMENT = useSelector(e => e.common?.pendingPayment);
    const _PAYMENT_METHOD_INFO = useSelector(e => e.common?.paymentMethodInfo);

    const { triggerPayment, isLoading: continueLoading } = useCashfreePayment();

    if (!_RESUME_PAYMENT) {
        // Show subtle card expiry warning when no other pending payment banner is active
        if (isCardExpiringSoon(_PAYMENT_METHOD_INFO)) {
            return (
                <div className="u_flex-align u_content-between u_py-10 u_px-20 u_bg-blue-1 u_bb-light">
                    <div className="u_flex-align">
                        <Icon.InfoTooltip size={18} fill={COLOR?.u_blue} className="u_mr-8" />
                        <p className="u_small-font u_color-title u_fw-500">
                            Your card is expiring soon. Update your payment method to avoid service interruption.
                        </p>
                    </div>
                    <UButton
                        type="Update Payment Method"
                        onClick={() => history.push(`/${_ORG_ID}/subscriptions/payment-method`)}
                        cn="u_ml-16 u_content-center u_payment-btn"
                    />
                </div>
            );
        }
        return null;
    }

    const { action, data, statusLabel } = _RESUME_PAYMENT;

    const handleContinuePayment = () => {
        triggerPayment(action, data);
    };

    if (action === 'RESUME_AUTH') {
        return (
            <div className="u_flex-align u_py-10 u_px-20 u_bg-lightorange u_bb-light">
                <div className="u_flex-align">
                    <Icon.Clock size={20} fill={COLOR?.u_orange} className="u_mr-8" />
                    <p className="u_small-font u_color-title u_fw-500">
                        {data?.message || 'Complete Your pending Subscription Payment'}
                    </p>
                </div>
                {statusLabel !== 'BANK_APPROVAL_PENDING' && <div>
                    <UButton
                        type={continueLoading ? 'Processing...' : 'Continue Payment'}
                        onClick={handleContinuePayment}
                        disabled={continueLoading}
                        cn="u_ml-16 u_content-center u_payment-btn"
                        style={{ display: 'flex', borderRadius: '6px', padding: '8px' }}
                    />
                </div>}
            </div>
        );
    }

    if (action === 'AUTH_SESSION_EXPIRED') {
        return (
            <div className="u_flex-align u_content-between u_py-10 u_px-20 u_bg-lightorange u_bb-light">
                <div className="u_flex-align">
                    <Icon.CloseMarkRound size={20} fill={COLOR?.u_red} className="u_mr-8" />
                    <p className="u_small-font u_color-title u_fw-500">
                        {data?.message || 'Session Expired'}
                    </p>
                </div>
                <div>
                    <UButton
                        type="Create Subscription"
                        onClick={() => history.push(`/${_ORG_ID}/manage/pricing-plan`)}
                        cn="u_ml-16 u_content-center u_payment-btn"
                        style={{ display: 'flex', borderRadius: '6px', padding: '8px' }}
                    />
                </div>
            </div>
        );
    }

    if (action === 'RESUME_PAYMENT' || action === 'RESUME_PAYMENT_METHOD_UPDATE') {
        return (
            <div className="u_flex-align u_py-10 u_px-20 u_bg-lightorange u_bb-light">
                <div className="u_flex-align">
                    <Icon.Clock size={20} fill={COLOR?.u_orange} className="u_mr-8" />
                    <p className="u_small-font u_color-title u_fw-500">
                        {data?.message || 'Complete Your pending Subscription Payment'}
                    </p>
                </div>
                <div>
                    <UButton
                        type={continueLoading ? 'Processing...' : 'Continue Payment'}
                        onClick={handleContinuePayment}
                        disabled={continueLoading}
                        cn="u_ml-16 u_content-center u_payment-btn"
                        style={{ display: 'flex', borderRadius: '6px', padding: '8px' }}
                    />
                </div>
            </div>
        );
    }

    if (action === 'WAIT_FOR_NEXT_CYCLE' || action === 'BANK_APPROVAL_PENDING') {
        return (
            <div className={`u_flex-align u_py-10 u_px-20 ${action === 'BANK_APPROVAL_PENDING' ? 'u_bg-lightorange' : 'u_bg-lightblue'} u_bb-light`}>
                {action === 'BANK_APPROVAL_PENDING' ? <Icon.Clock size={20} fill={COLOR?.u_orange} className="u_mr-8" /> : <Icon.InfoTooltip size={18} fill={COLOR?.u_blue} className="u_mr-8" />}
                <p className="u_small-font u_color-title u_fw-500">
                    {data?.message || 'Plan Change Scheduled'}
                </p>
            </div>
        );
    }

    if (action === 'PAYMENT_TERMINAL_RETRY_MODIFY') {
        return (
            <div className="u_flex-align u_content-between u_py-10 u_px-20 u_bg-thead u_bb-light">
                <div className="u_flex-align">
                    <Icon.CloseMarkRound size={20} fill={COLOR?.u_red} className="u_mr-8" />
                    <p className="u_small-font u_color-title u_fw-500">
                        {data?.message || 'Your previous payment has expired. Please try upgrading again'}
                    </p>
                </div>
                <div>
                    <UButton
                        type="Upgrade Again"
                        onClick={() => history.push(`/${_ORG_ID}/manage/pricing-plan`)}
                        cn="u_ml-16 u_content-center u_payment-btn"
                        style={{ display: 'flex', borderRadius: '6px', padding: '8px' }}
                    />
                </div>
            </div>
        );
    }

    return null;
};

export default ResumePaymentBanner;
